/**
 * Framework-free utilities shared between pdfGenerator.ts (browser) and
 * generate-pdfs.ts (Node.js script). No React, Gatsby, or zustand imports.
 */

// ---------------------------------------------------------------------------
// Interfaces
// ---------------------------------------------------------------------------

export interface FormCondition {
  field?: string | null;
  condition?: string | null;
  value?: string | null;
}

export interface TableColumn {
  id?: string | null;
  label?: string | null;
  type?: string | null;
  required?: boolean | null;
  maxCharacters?: number | null;
}

export interface PermitFormField {
  _key?: string;
  id?: string | null;
  _type?: string;
  label?: string | null;
  type?: string | null;
  options?: (string | null)[] | null;
  fullWidth?: boolean;
  required?: boolean | null;
  maxCharacters?: number | null;
  accept?: string | null;
  tableColumns?: (TableColumn | null)[] | null;
  hidden?: { field?: string | null; condition?: string | null; value?: string | null } | null;
}

// ---------------------------------------------------------------------------
// Field key resolution
// ---------------------------------------------------------------------------

/** Key used for input name, form state and conditions. Prefer id over _key. */
export function getFieldKey(
  field: { id?: string | null; _key?: string | null } | null | undefined,
  fallbackIndex?: number,
): string {
  const key = field?.id ?? field?._key;
  if (key != null && key !== "") return key;
  return fallbackIndex !== undefined ? `field-${fallbackIndex}` : `field-${Math.random()}`;
}

// ---------------------------------------------------------------------------
// Field visibility
// ---------------------------------------------------------------------------

function isValidCompleteDate(val: string): boolean {
  if (!val || typeof val !== "string") return false;
  const date = new Date(val);
  const now = new Date();
  const hundredYearsAgo = new Date(now.getFullYear() - 100, 0, 1);
  const tenYearsFromNow = new Date(now.getFullYear() + 10, 11, 31);
  return !isNaN(date.getTime()) && date >= hundredYearsAgo && date <= tenYearsFromNow;
}

export function evaluateCondition(
  fieldValue: string | boolean | string[],
  condition: string,
  compareValue: string,
): boolean {
  const val = Array.isArray(fieldValue) ? fieldValue.join(",") : String(fieldValue ?? "");

  if (condition === "equal") return val === compareValue;
  if (condition === "notEqual") return val !== compareValue;

  const numVal = parseFloat(val);
  const numCompare = parseFloat(compareValue);
  if (!isNaN(numVal) && !isNaN(numCompare)) {
    if (condition === "greaterThan") return numVal > numCompare;
    if (condition === "lessThan") return numVal < numCompare;
    if (condition === "greaterThanOrEqual") return numVal >= numCompare;
    if (condition === "lessThanOrEqual") return numVal <= numCompare;
  }

  if (condition.startsWith("date") && isValidCompleteDate(val)) {
    const date = new Date(val);
    const compareNum = parseFloat(compareValue);
    if (isNaN(date.getTime()) || isNaN(compareNum)) return false;
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();

    if (condition === "dateOlderThanYears") return diffMs / (1000 * 60 * 60 * 24 * 365.25) > compareNum;
    if (condition === "dateNewerThanYears") return diffMs / (1000 * 60 * 60 * 24 * 365.25) < compareNum;
    if (condition === "dateOlderThanDays")  return diffMs / (1000 * 60 * 60 * 24) > compareNum;
    if (condition === "dateNewerThanDays")  return diffMs / (1000 * 60 * 60 * 24) < compareNum;
  }

  return false;
}

/** Returns true when the field should be hidden (condition is met). */
export function isFieldHidden(
  field: { hidden?: FormCondition | null } | null,
  inputs: Record<string, unknown>,
): boolean {
  if (!field?.hidden?.field || !field.hidden.condition || field.hidden.value == null) return false;
  if (!Object.prototype.hasOwnProperty.call(inputs, field.hidden.field)) return false;
  const watchedValue = inputs[field.hidden.field];
  return evaluateCondition(
    watchedValue as string | boolean | string[],
    field.hidden.condition,
    String(field.hidden.value),
  );
}
